import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import ConditionalField from 'components/ConditionalField'
import Typography from '@mui/material/Typography'
import { ibanFieldConfig } from 'utils/iban'
import { Grid, GridProps, Theme, useMediaQuery } from '@mui/material'
import Box, { BoxProps } from '@mui/material/Box'
import Seal from 'components/Footer/Seal'
import { TuevSealComponent } from 'components/Footer/getSeals'
import { toJS } from 'mobx'
import { observer } from 'mobx-react'
import AccountType from 'utils/formDependencies/statics/enums/AccountType'
import { Link } from 'react-router-dom'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import { isInABTestDeleteIBANTextVariantB } from 'utils/tracking/sitespect/campaigns'
import { getPathIntroductionData } from 'utils/getPathIntroductionData'
import { getIntroductionStylesVariant } from 'utils/getIntroductionStylesVariant'
import { useText } from 'stores/utils/hooks/useText'
import useEmotionStyledComponent from 'hooks/useEmotionStyledComponent'
import Notice, { NoticeType } from '../components/Notice'
import {
  ActiveLoansItem,
  PayoutAccount as PayoutAccountInterface
} from '../interfaces/ApolloResponse.interface'
import { useStores } from '../stores/utils/hooks/useStores'
import AccountHolder from '../components/AccountHolder'
import PathRadioGroup from '../components/PathChoiceFieldGroup'
import PathInput from '../components/PathInput'
import PathIntroduction from '../components/PathIntroduction'
import PathSelect from '../components/PathSelect'

export const IBAN_CONSTRAINTS = {
  minLength: 18,
  doNotInvokeBlurOnChange: true
}

// eslint-disable-next-line react/display-name
const IntroText = memo(() => (
  <div>
    <p>
      Wir buchen kein Geld von Ihrem Konto ab. Ihre Bankverbindung wird
      lediglich zur Auszahlung Ihres Wunschkredits benötigt.
      <br />
      <strong>Wichtig:</strong> Mit der Angabe Ihrer IBAN findet{' '}
      <u>noch kein</u> Kreditabschluss statt.
    </p>
    <p>
      Ob und bei welcher Bank Sie einen Kredit abschließen möchten, entscheiden
      Sie. Hierzu erhalten Sie im nächsten Schritt eine transparente Übersicht
      der besten für Sie persönlich erstellten Angebote.
    </p>
  </div>
))

const SealGrid = (props: GridProps) => {
  const StyledSealGrid = useEmotionStyledComponent<typeof Grid, GridProps>(
    (theme: Theme) => ({
      root: {
        marginTop: theme.spacing(1.5),
        flex: 1,
        '& img': {
          height: '45px'
        }
      }
    }),
    Grid,
    props
  )
  return <StyledSealGrid {...props} />
}

const SealBox = (props: BoxProps) => {
  const StyledSealBox = useEmotionStyledComponent<typeof Box, BoxProps>(
    (theme: Theme) => ({
      root: {
        backgroundColor: theme.palette.grey[200]
      }
    }),
    Box,
    props
  )
  return <StyledSealBox {...props} />
}

const bankAccountEquals = (
  activeLoans: ActiveLoansItem[],
  payoutAccount: PayoutAccountInterface
) =>
  activeLoans.some((account) => {
    const { bankAccount } = account

    if (bankAccount.type !== payoutAccount.type) {
      return false
    }

    switch (payoutAccount.type) {
      case AccountType.IBAN_BIC:
        return bankAccount.iban === payoutAccount.iban
      case AccountType.ACCOUNTNUMBER_BANKCODE:
        return (
          bankAccount.accountNumber === payoutAccount.accountNumber &&
          bankAccount.bankCode === payoutAccount.bankCode
        )
      default:
        return false
    }
  })

const PayoutAccount = observer(() => {
  const tuevSeal = TuevSealComponent()
  const {
    text: { copy }
  } = useText()

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))

  const { traversal, history } = useStores()
  const {
    common: { payoutAccount },
    debtors: {
      primary: { activeLoans: primaryActiveLoans },
      secondary: { activeLoans: secondaryActiveLoans }
    }
  } = traversal.data

  const showBankAccountNotice = useMemo(
    () =>
      bankAccountEquals(
        [
          ...toJS(primaryActiveLoans || []),
          ...toJS(secondaryActiveLoans || [])
        ],
        payoutAccount
      ),
    [payoutAccount, primaryActiveLoans, secondaryActiveLoans]
  )

  const [displayBankAccountNotice, setDisplayBankAccountNotice] =
    useState(false)

  const handleCloseNotice = useCallback(
    () => setDisplayBankAccountNotice(false),
    [setDisplayBankAccountNotice]
  )

  useEffect(() => {
    setDisplayBankAccountNotice(showBankAccountNotice)

    if (showBankAccountNotice) {
      trackInAmplitude(AmplitudeEvent.AlertSamePayoutAccountUsed, {
        payoutAccountType: payoutAccount.type
      })
    }
  }, [showBankAccountNotice, payoutAccount.type])

  const replaceHistory = () => {
    history.replace('activeLoans')
  }

  return (
    <ConditionalField path="common.payoutAccount.type">
      <Typography variant="h2">{copy?.headline?.payoutAccount}</Typography>
      {!isInABTestDeleteIBANTextVariantB() && (
        <PathIntroduction
          path="copy.introduction.loanPayout.easyPayout"
          {...getPathIntroductionData({
            copyIntroduction: copy.introduction,
            stylesVariant: getIntroductionStylesVariant({})
          })}
        >
          <IntroText />
        </PathIntroduction>
      )}
      <AccountHolder />
      <Grid container alignItems="center">
        <Grid item>
          <PathRadioGroup path="common.payoutAccount.type" />
        </Grid>
        <SealGrid
          item
          container
          justifyContent={isMobile ? 'center' : 'flex-end'}
        >
          {tuevSeal && (
            <Grid item>
              <SealBox
                padding={1}
                border={1}
                borderColor="grey.400"
                borderRadius={6}
              >
                <Seal name={tuevSeal.name} url={tuevSeal.url}>
                  {tuevSeal.children}
                </Seal>
              </SealBox>
            </Grid>
          )}
        </SealGrid>
      </Grid>
      <PathSelect path="common.payoutAccount.country" />
      <PathInput path="common.payoutAccount.accountNumber" isNumericField />
      <PathInput path="common.payoutAccount.bankCode" isNumericField />
      <PathInput
        path="common.payoutAccount.iban"
        onChangeConstraints={IBAN_CONSTRAINTS}
        clue={copy?.clue?.loanPayout?.payoutAccount}
        fieldConfig={ibanFieldConfig}
        delayInMs={0}
      />
      {displayBankAccountNotice && (
        <Notice
          type={NoticeType.WARNING}
          gutterBottom
          onClose={handleCloseNotice}
        >
          Die Kontodaten für die Kreditauszahlung sollten nicht dieselben sein,
          wie die Kontodaten für Ihre{' '}
          <Link to="../activeLoans" onClick={replaceHistory}>
            umzuschuldenden Kredite.
          </Link>{' '}
          <br />
          Bitte korrigieren Sie Ihre Eingabe.
        </Notice>
      )}
    </ConditionalField>
  )
})

export default memo(PayoutAccount)
